// The code set up is used for creating the authentication context in React using the Context API.
// It creates an AuthContext using createContext() to manage the authentication state.

// The AuthProvider component is designed to wrap the application and provide the authentication context to its child components using the AuthContext.Provider
import { useContext, createContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  OTP_VERIFICATION_ENDPOINT,
  REFRESH_TOKEN_ENDPOINT,
  SELECT_TENANT_ENDPOINT,
  REFRESH_PERMISSIONS_ENDPOINT
} from "../config/urls"; // Added refresh token endpoint
import { jwtDecode } from "jwt-decode"; // Corrected import
import dayjs from "dayjs"; // Used to handle token expiration time

const AUTO_LOGOUT_MINUTES = 60;

const DEFAULT_TENANT_TIMEZONE = "Asia/Kolkata";

// tenant_timezone is a custom JWT claim (api/tokens.py) reflecting the
// tenant's configured timezone at the time the token was issued. Falls back
// to the platform's historical default for tokens issued before the claim
// existed, or if it's ever missing/empty.
const storeTenantTimezone = (decodedToken) => {
  localStorage.setItem(
    "tenantTimezone",
    decodedToken.tenant_timezone || DEFAULT_TENANT_TIMEZONE
  );
};

const HISTORY_FLOOR_KEY = "auth_history_floor_idx";

if (!window.__authHistoryPatched) {
  window.__authHistoryPatched = true;
  let navCounter = window.history.state?.__navIdx ?? 0;
  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = (state, title, url) => {
    navCounter += 1;
    return originalPushState({ ...state, __navIdx: navCounter }, title, url);
  };

  window.history.replaceState = (state, title, url) => {
    const currentIdx = window.history.state?.__navIdx ?? navCounter;
    return originalReplaceState({ ...state, __navIdx: currentIdx }, title, url);
  };
}

const markHistoryFloor = () => {
  const idx = window.history.state?.__navIdx ?? 0;
  sessionStorage.setItem(HISTORY_FLOOR_KEY, String(idx));
};

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem("refresh_token") || ""
  ); // Storing refresh token
  const [permissions, setPermissions] = useState([]);
  const navigate = useNavigate();
  const prevTokenRef = useRef(token);

  useEffect(() => {
    if (!prevTokenRef.current && token) {
      markHistoryFloor();
    }
    prevTokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUser(decodedToken.user);
      setPermissions(decodedToken.permissions);

      // Check if the token is about to expire and refresh it
      const tokenExpiry = dayjs.unix(decodedToken.exp);
      if (tokenExpiry.diff(dayjs()) < 1) {
        refreshAccessToken();
      }
    }
  }, [token]);

  // Auto Logout in case of inactivity
  const timeoutRef = useRef(null);

  // Throttle function to limit the frequency of resetTimer calls
  const throttle = (fn, limit) => {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  };

  const resetTimer = (e) => {
    // console.log("Resetting auto logout timer", e ? `due to ${e.type}` : "initialization");
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (localStorage.getItem("token")) {
        logOut();
      }
    }, AUTO_LOGOUT_MINUTES * 60 * 1000);
  };

  useEffect(() => {
    const handlePopState = (event) => {
      const floor = Number(sessionStorage.getItem(HISTORY_FLOOR_KEY));
      const targetIdx = event.state?.__navIdx;
      if (
        !Number.isNaN(floor) &&
        sessionStorage.getItem(HISTORY_FLOOR_KEY) !== null &&
        typeof targetIdx === "number" &&
        targetIdx < floor
      ) {
        window.history.go(floor - targetIdx);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const throttledResetTimer = throttle(resetTimer, 1000);

    const events = ["mousemove", "keydown", "click"];
    events.forEach((event) =>
      window.addEventListener(event, throttledResetTimer)
    );

    resetTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, throttledResetTimer)
      );
      clearTimeout(timeoutRef.current);
    };
  }, []);

  // Function to refresh access token
  const refreshAccessToken = async () => {
    try {
      const response = await fetch(REFRESH_TOKEN_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });
      const res = await response.json();

      if (response.status === 200) {
        setToken(res.access);
        localStorage.setItem("token", res.access);

        const decodedToken = jwtDecode(res.access);
        setUser(decodedToken.user);
        setPermissions(decodedToken.permissions);
        localStorage.setItem("code", decodedToken.code);
        storeTenantTimezone(decodedToken);
      } else {
        logOut();
      }
    } catch (err) {
      logOut();
    }
  };

  // Recompute permissions/apps/features/plan from the DB (e.g. right after a
  // plan purchase activates) without requiring a fresh login or JWT re-issue.
  const refreshPermissions = async () => {
    try {
      const validToken = await getValidToken();
      const response = await fetch(REFRESH_PERMISSIONS_ENDPOINT, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${validToken}`,
          "X-Tenant-ID": localStorage.getItem("tenant") || "",
        },
      });
      const res = await response.json();

      if (response.status === 200) {
        setPermissions(res.permissions);
        localStorage.setItem("permissions", JSON.stringify(res.permissions));
        localStorage.setItem("apps", JSON.stringify(res.apps));
        localStorage.setItem("features", JSON.stringify(res.features));
        localStorage.setItem("plan", res.plan);
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      return { success: false };
    }
  };

  // Get a valid token, refresh it if it's expired
  const getValidToken = async () => {
    const decodedToken = jwtDecode(token);
    const tokenExpiry = dayjs.unix(decodedToken.exp);

    if (tokenExpiry.diff(dayjs()) < 1) {
      await refreshAccessToken(); // Refresh token if expired
    }
    return localStorage.getItem("token"); // Return the latest token
  };

  const verifyOtpUrl = OTP_VERIFICATION_ENDPOINT;

  const loginAction = async (data) => {
    try {
      const response = await fetch(verifyOtpUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const res = await response.json();
      // console.log("Login Response:", res); // Debugging

      if (response.status === 200) {
        if (res.multiple_tenants) {
          setToken(res.access);
          setRefreshToken(res.refresh);
          localStorage.setItem("token", res.access);
          localStorage.setItem("refresh_token", res.refresh);
          localStorage.setItem("multiple_tenants", true);
          localStorage.setItem("possible_tenants", JSON.stringify(res.tenants));
          localStorage.setItem("global_user_id", res.user_id);
          
          navigate("/select-tenant", {
            replace: true,
            state: { tenants: res.tenants, user_id: res.user_id },
          });
          return { msg: "Valid OTP", type: "success" };
        }

        setToken(res.access);
        setRefreshToken(res.refresh);
        localStorage.setItem("token", res.access);
        localStorage.setItem("refresh_token", res.refresh);
        localStorage.setItem("role", res.role);

        const decodedToken = jwtDecode(res.access);
        // console.log("Decoded Token:", decodedToken); // Debugging

        setUser(res.user);
        setPermissions(decodedToken.permissions);
        localStorage.setItem("code", decodedToken.code);
        localStorage.setItem(
          "permissions",
          JSON.stringify(decodedToken.permissions)
        );
        localStorage.setItem("user", JSON.stringify(res.user));
        localStorage.setItem("tenant", res.tenant);
        localStorage.setItem("features", JSON.stringify(res.features));
        localStorage.setItem("apps", JSON.stringify(res.apps));
        localStorage.setItem("plan", decodedToken.plan);
        storeTenantTimezone(decodedToken);

        if (res.tenant) {
          console.log("Redirecting to tenant:", res.tenant); // Debugging
          // redirectToTenantDomain(res.tenant);
          navigate("/", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
        return { msg: "Valid OTP", type: "success" };
      }
      throw new Error(res.msg);
    } catch (err) {
      console.error("Login Error:", err);
      return { msg: err.message || "Invalid OTP", type: "error" };
    }
  };

  const selectTenant = async (data) => {
    try {
      const response = await fetch(SELECT_TENANT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      
      const res = await response.json();

      if (response.status === 200) {
        setToken(res.access);
        setRefreshToken(res.refresh);
        localStorage.setItem("token", res.access);
        localStorage.setItem("refresh_token", res.refresh);
        localStorage.setItem("role", res.role);

        console.log("Access: ", res.access);
        const decodedToken = jwtDecode(res.access);
        console.log("Decoded Token:", decodedToken); // Debugging

        setUser(res.user);
        setPermissions(decodedToken.permissions);
        localStorage.setItem("code", decodedToken.code);
        localStorage.setItem(
          "permissions",
          JSON.stringify(decodedToken.permissions)
        );
        localStorage.setItem("user", JSON.stringify(res.user));
        localStorage.setItem("tenant", res.tenant);
        localStorage.setItem("features", JSON.stringify(res.features));
        localStorage.setItem("apps", JSON.stringify(res.apps));
        localStorage.setItem("plan", decodedToken.plan);
        storeTenantTimezone(decodedToken);

        if (res.tenant) {
          console.log("Redirecting to tenant:", res.tenant); // Debugging
          // redirectToTenantDomain(res.tenant);
          navigate("/", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
        return { msg: "Valid OTP", type: "success" };
      }
      throw new Error(res.msg);
    } catch (err) {
      console.error("Login Error:", err);
      return { msg: err.message || "Invalid OTP", type: "error" };
    }
  };

  const logOut = () => {
    setUser(null);
    setToken("");
    setRefreshToken("");
    setPermissions([]);
    localStorage.clear();
    sessionStorage.removeItem(HISTORY_FLOOR_KEY);
    navigate("/login", { replace: true });
  };

  const redirectToSwitchTenant = () => {
    const tenants = JSON.parse(localStorage.getItem("possible_tenants"));
    const global_user_id = localStorage.getItem("global_user_id");

    if(!tenants || !global_user_id) return

    setUser(null);
    setPermissions([]);

    // localStorage.removeItem("role")
    // localStorage.removeItem("code")
    // localStorage.removeItem("permissions")
    // localStorage.removeItem("user")
    // localStorage.removeItem("tenant")
    // localStorage.removeItem("features")
    // localStorage.removeItem("apps")
    // localStorage.removeItem("MAX_USER_COUNT")
    // localStorage.removeItem("plan")

    navigate("/select-tenant", {
      replace: true,
      state: { tenants: tenants, user_id: global_user_id },
    });
  }

  const redirectToTenantDomain = (subdomain) => {
    const protocol = window.location.protocol;
    const host = window.location.host;

    console.log("Current Host:", host);
    console.log("Subdomain:", subdomain);

    let newDomain;
    if (host.includes("localhost") || host.includes("127.0.0.1")) {
      newDomain = `${subdomain}.localhost:3000`;
    } else {
      const domain = host.split(".").slice(1).join(".");
      newDomain = `${subdomain}.${domain}`;
    }

    console.log("Redirecting to:", `${protocol}//${newDomain}`);

    window.location.href = `${protocol}//${newDomain}`;
  };

  const getRole = () => {
    return localStorage.getItem("role") ?? "viewer"
  }

  return (
    <AuthContext.Provider
      value={{ token, user, permissions, loginAction, selectTenant, logOut, getValidToken, redirectToSwitchTenant, getRole, refreshPermissions }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
